/**
 * DonutChart: one arc per category around a shared centre, drawn from plain data
 * (ADR-026) on Recharts. The centre carries the total; every arc names itself;
 * the single-slice degenerate arc is solved by the library's angle model.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { Pie, PieChart, Tooltip } from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, DONUT_SIZE, Legend, seriesClass, surfaceClass } from "./shared";

export interface DonutDatum {
  readonly label: string;
  readonly value: number;
}

const CENTER = DONUT_SIZE / 2;
const OUTER_RADIUS = 108;
const THICKNESS = 36;
const INNER_RADIUS = OUTER_RADIUS - THICKNESS;

interface SectorShapeProps {
  readonly cx?: number;
  readonly cy?: number;
  readonly innerRadius?: number;
  readonly outerRadius?: number;
  readonly startAngle?: number;
  readonly endAngle?: number;
  readonly index?: number;
  readonly payload?: DonutDatum;
}

export function DonutChart({
  ariaLabel,
  data,
  centreLabel,
  onSelectDatum,
}: {
  ariaLabel: string;
  data: readonly DonutDatum[];
  /** The word under the centre total, e.g. "records". */
  centreLabel: string;
  /** Optional click-through (the chart contract): the picked datum. */
  onSelectDatum?: (datum: DonutDatum, index: number) => void;
}): React.JSX.Element {
  const total = data.reduce((sum, datum) => sum + datum.value, 0);
  if (data.length === 0 || total === 0) {
    return <ChartEmpty />;
  }

  const sector = (props: SectorShapeProps): React.JSX.Element | null => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, index, payload } = props;
    if (
      cx === undefined ||
      cy === undefined ||
      innerRadius === undefined ||
      outerRadius === undefined ||
      startAngle === undefined ||
      endAngle === undefined
    ) {
      return null;
    }
    const i = index ?? 0;
    const name =
      payload === undefined ? undefined : strings.charts.mark(payload.label, payload.value);
    const clickable =
      onSelectDatum === undefined || payload === undefined
        ? undefined
        : () => {
            onSelectDatum(payload, i);
          };
    const className =
      onSelectDatum === undefined
        ? `${seriesClass(i)} q-chart__arc`
        : `${seriesClass(i)} q-chart__arc q-chart__hit cursor-pointer`;
    const midRadius = (outerRadius + innerRadius) / 2;
    const point = (radius: number, angle: number): { x: number; y: number } => ({
      x: cx + radius * Math.sin((angle * Math.PI) / 180),
      y: cy - radius * Math.cos((angle * Math.PI) / 180),
    });
    const common = {
      className,
      fill: "none",
      strokeWidth: outerRadius - innerRadius,
      role: "img" as const,
      "aria-label": name,
      tabIndex: onSelectDatum === undefined ? undefined : 0,
      onClick: clickable,
      onKeyDown:
        clickable === undefined
          ? undefined
          : (event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                clickable();
              }
            },
    };
    if (endAngle - startAngle >= 359.999) {
      // The degenerate single-slice arc: a full turn is a ring, not an arc.
      return (
        <circle {...common} cx={cx} cy={cy} r={midRadius}>
          {name === undefined ? null : <title>{name}</title>}
        </circle>
      );
    }
    const start = point(midRadius, startAngle);
    const end = point(midRadius, endAngle);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    const d = `M ${String(start.x)} ${String(start.y)} A ${String(midRadius)} ${String(midRadius)} 0 ${String(large)} 1 ${String(end.x)} ${String(end.y)}`;
    return (
      <path {...common} d={d}>
        {name === undefined ? null : <title>{name}</title>}
      </path>
    );
  };

  return (
    <figure className="q-chart" data-testid="donut-chart">
      <div className="q-chart__frame q-chart__frame--square">
        <PieChart
          width={DONUT_SIZE}
          height={DONUT_SIZE}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          role="img"
          aria-label={ariaLabel}
          ref={surfaceClass}
        >
          <Tooltip
            isAnimationActive={false}
            content={({ active: hover, payload }) =>
              hover && payload.length > 0 ? (
                <div className="q-chart__tooltip">
                  <ul className="q-chart__tooltip-list">
                    {payload.map((entry, index) => (
                      <li key={index} className="q-chart__tooltip-row">
                        <span
                          aria-hidden="true"
                          className={`q-chart__tooltip-swatch ${seriesClass(index)}`}
                        />
                        {entry.name}: {String(entry.value)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null
            }
          />
          <Pie
            data={[...data]}
            dataKey="value"
            nameKey="label"
            cx={CENTER}
            cy={CENTER}
            innerRadius={INNER_RADIUS}
            outerRadius={OUTER_RADIUS}
            startAngle={0}
            endAngle={360}
            isAnimationActive={false}
            shape={sector}
          />
          <text
            className="q-chart__tick"
            x={CENTER}
            y={CENTER - 4}
            textAnchor="middle"
            style={{ fontSize: "var(--q-s1)", fill: "var(--q-ink)" }}
          >
            {String(total)}
          </text>
          <text className="q-chart__tick" x={CENTER} y={CENTER + 16} textAnchor="middle">
            {centreLabel}
          </text>
        </PieChart>
      </div>
      <Legend entries={data.map((datum, index) => ({ label: datum.label, index }))} />
    </figure>
  );
}
